import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import type { Site } from "@/models/site"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"
import { useInvalidateQueries } from "../utils/useInvalidateQueries"
import { useQueryKey } from "../utils/useQueryKey"

/**
 * temporarily used for updating site on the rec and planning screen for setting goals
 * and updating metadata about a site.
 *
 */
export const useUpdateSiteModel = () => {
  const queryClient = useQueryClient()
  const mutationKey = useQueryKey(ApiQueryName.SiteUpdate, "updateOne")
  const { invalidateQueries } = useInvalidateQueries()
  const { setNotification } = useNotification()
  const { t } = useTranslation("common")

  const mutationResult = useMutation({
    meta: { handlesOwnErrors: true },
    mutationKey,
    mutationFn: async (vars: { site: Site; with?: string[] }) => {
      return await vars.site.save({
        with: vars.with,
      })
    },
    onSettled: (data, error, vars) => {
      // Handle success
      if (data === true) {
        invalidateQueries([
          createQueryKey(ApiQueryName.Site, "getOne", vars.site.id),
          createQueryKey(ApiQueryName.Sites, "getMany"),
        ])

        queryClient
          .invalidateQueries({ queryKey: [ApiQueryName.SiteExplorer] })
          .catch((e: unknown) => {
            if (e instanceof Error) {
              console.error(e)
            }
          })
      }

      // Handle error
      // Spraypaint successfully returns 422 data as false
      // https://www.graphiti.dev/js/writes/
      if (data === false || error) {
        setNotification({
          message: getErrorNotificationMessage(
            error,
            t("editSite.updateSiteError")
          ),
          type: "error",
        })
      }
    },
  })

  return useRenameQueryResult(mutationResult, ApiQueryName.SiteUpdate)
}
